import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'

import {
  ContractorFilingJobSubstep,
  fetchUserYearEndModuleStatuses,
  FormFilingJobSubstep,
  YearEndModuleStatusOptions,
  YearEndModuleType,
} from '../yearEndModuleStatus.slice'
import YearEndModuleBaseCard, {
  defaultDeadlineApproaching,
  YearEndModuleStatusDynamicDataParams,
} from './YearEndModuleStatusBaseCard'
import { Button, Link, Text } from '@/componentsV1/BaseComponents'
import { Colors } from '../../../styles/theme'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useFetchResponseWithUtils, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  selectCurrentAnnualTaxDetails,
  selectCurrentAnnualTaxYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  getModuleNameCopy,
  selectAvailableAfterTextFor,
} from '@/features/year-end-module-status/yearEndModuleStatus.selectors'
import {
  get1099NecMagicLink,
  getContractorFilingJob,
  REQUEST_1099_MAGIC_LINK_KEY,
  START_CONTRACTOR_FILING_JOB_KEY,
  startContractorFilingJob,
} from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { useOpenTaxfylePortal } from '@/features/taxes/annual-taxes/useOpenTaxfylePortal'
import { TaxfylePortalError } from '@/features/taxes/annual-taxes/components/TaxfylePortalError'
import { getFetchError } from '@/reducers/fetch'
import { Alert } from '@/ui'
import { TaxfyleJobStatus } from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import {
  CONTRACTOR_FILING_QUESTIONNAIRE_PATH,
  get1099NecThresholdDisplay,
} from '@/features/taxes/annual-taxes/constants'

const StandardDescription = ({
  disabled,
  taxYear,
}: {
  disabled?: boolean
  taxYear: string
}) => (
  <Text as="bodySm" style={{ color: disabled ? Colors.mediumGray : undefined }}>
    If you paid one or more contractors {get1099NecThresholdDisplay(taxYear)} or
    more in {taxYear}, you&apos;ll need to file a 1099-NEC. Answer a few
    questions and a Taxfyle preparer will file them for you. Heard covers the
    full cost.
  </Text>
)

enum FilingStage {
  notStarted = 'notStarted',
  questionnaire = 'questionnaire',
  awaitingPreparer = 'awaitingPreparer',
  needsReply = 'needsReply',
  submitted = 'submitted',
}

const BUTTON_TEXT: Record<FilingStage, string> = {
  [FilingStage.notStarted]: 'Get started',
  [FilingStage.questionnaire]: 'Continue',
  [FilingStage.awaitingPreparer]: 'Go to Taxfyle',
  [FilingStage.needsReply]: 'Go to Taxfyle',
  [FilingStage.submitted]: 'Go to Taxfyle',
}

const STAGES_WITH_CUSTOMER_WORK: FilingStage[] = [
  FilingStage.notStarted,
  FilingStage.questionnaire,
]

const BUTTON_STATUSES = [
  YearEndModuleStatusOptions.upNext,
  YearEndModuleStatusOptions.actionRequired,
  YearEndModuleStatusOptions.inProgress,
  YearEndModuleStatusOptions.overdue,
]

const UNCLAIMED_JOB_STATUSES = [
  TaxfyleJobStatus.infoGathering,
  TaxfyleJobStatus.unclaimed,
]

const File1099NECCard = ({
  status,
  dueDate,
  completedAt,
  substepIdentifier,
}: YearEndModuleStatusDynamicDataParams) => {
  const dispatch = useAppDispatch()
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const currentDetails = useReselector(selectCurrentAnnualTaxDetails)
  const availableAfterText = useReselector(
    selectAvailableAfterTextFor,
    YearEndModuleType.file1099Nec
  )

  const {
    open: openPortal,
    loading: portalLoading,
    error: portalError,
  } = useOpenTaxfylePortal(get1099NecMagicLink, REQUEST_1099_MAGIC_LINK_KEY)
  const {
    result: jobResult,
    refetch: refetchJob,
    loading: isJobLoading,
  } = useFetchResponseWithUtils(getContractorFilingJob)
  const navigate = useNavigate()
  const [isStarting, setIsStarting] = useState(false)
  const startError = useReselector(
    getFetchError,
    START_CONTRACTOR_FILING_JOB_KEY
  )

  const stage = useMemo<FilingStage>(() => {
    if (
      substepIdentifier === ContractorFilingJobSubstep.questionnaireRequired
    ) {
      return FilingStage.questionnaire
    }
    if (substepIdentifier === FormFilingJobSubstep.notClaimed) {
      return FilingStage.awaitingPreparer
    }
    if (substepIdentifier === FormFilingJobSubstep.inProgressNeedsUserAction) {
      return FilingStage.needsReply
    }
    const job = jobResult?.job
    if (!job) {
      return FilingStage.notStarted
    }
    if (job.jobStatus === TaxfyleJobStatus.underConstruction) {
      return FilingStage.questionnaire
    }
    return UNCLAIMED_JOB_STATUSES.includes(job.jobStatus)
      ? FilingStage.awaitingPreparer
      : FilingStage.submitted
  }, [jobResult, substepIdentifier])

  const refreshCard = useCallback(async () => {
    await Promise.all([
      refetchJob(),
      fetchUserYearEndModuleStatuses()(dispatch),
    ])
  }, [dispatch, refetchJob])

  const onActionClick = useCallback(async () => {
    if (stage === FilingStage.submitted || stage === FilingStage.needsReply) {
      await openPortal()
      return
    }
    if (stage === FilingStage.notStarted) {
      setIsStarting(true)
      const started = await startContractorFilingJob()(dispatch)
      await refreshCard()
      setIsStarting(false)
      // Start returns an existing job too; only an unsubmitted one needs answers.
      if (started?.job.jobStatus !== TaxfyleJobStatus.underConstruction) {
        return
      }
    }
    navigate(CONTRACTOR_FILING_QUESTIONNAIRE_PATH)
  }, [dispatch, navigate, openPortal, refreshCard, stage])

  const description = useMemo(() => {
    // No preparer to talk to yet, so the card says so instead of linking to Taxfyle.
    if (stage === FilingStage.awaitingPreparer) {
      return <Text as="bodySm">We&apos;re connecting you with a preparer.</Text>
    }
    switch (status) {
      case YearEndModuleStatusOptions.waitingOnHeard:
      case YearEndModuleStatusOptions.overdue:
      case YearEndModuleStatusOptions.upNext:
      case YearEndModuleStatusOptions.actionRequired:
      case YearEndModuleStatusOptions.inProgress: {
        if (stage === FilingStage.questionnaire) {
          return (
            <Text as="bodySm">
              You&apos;ve started your 1099-NEC filing. Answer the remaining
              questions so your preparer can begin.
            </Text>
          )
        }
        if (stage === FilingStage.needsReply) {
          return (
            <Text as="bodySm">
              Your preparer has questions about your 1099-NECs. Reply in Taxfyle
              so they can finish your filing.
            </Text>
          )
        }
        if (stage === FilingStage.submitted) {
          return (
            <Text as="bodySm">
              Your preparer is working on your 1099-NECs. Check Taxfyle for
              messages or document requests.
            </Text>
          )
        }
        return <StandardDescription taxYear={taxYear} />
      }
      case YearEndModuleStatusOptions.complete: {
        return (
          <>
            <Text as="bodySm">
              Completed on{' '}
              <b>
                {convertUtcToLocalDate(completedAt)?.toFormat(
                  DATE_FORMATS_LUXON.MONTH_DAY
                )}
              </b>
              . If this was a mistake, you can still get 1099s through{' '}
              <Button
                variant="link"
                inline
                style={{
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  color: Colors.green,
                }}
                loading={portalLoading}
                onClick={() => {
                  void openPortal()
                }}
              >
                Taxfyle
              </Button>
              .
            </Text>
            <TaxfylePortalError error={portalError} />
          </>
        )
      }
      case YearEndModuleStatusOptions.upcoming: {
        return (
          <>
            <StandardDescription disabled taxYear={taxYear} />
            <br />
            <Text as="bodyXs" style={{ color: Colors.mediumGray }}>
              <b>{availableAfterText}</b>
            </Text>
            <br />
            <Button disabled>Coming Soon</Button>
          </>
        )
      }
      case YearEndModuleStatusOptions.missed:
      case YearEndModuleStatusOptions.earlyStart:
      case YearEndModuleStatusOptions.cancelled:
        return null
      default:
        return status satisfies never
    }
  }, [
    completedAt,
    status,
    taxYear,
    availableAfterText,
    stage,
    openPortal,
    portalLoading,
    portalError,
  ])

  return (
    <YearEndModuleBaseCard
      key={YearEndModuleType.file1099Nec}
      status={status}
      dueDate={dueDate}
      taxYear={taxYear}
      header={getModuleNameCopy(YearEndModuleType.file1099Nec)}
      imageUrl="https://heard-images.s3.amazonaws.com/assets/form-fill.svg"
      description={
        startError && stage === FilingStage.notStarted ? (
          <>
            {description}
            <Alert variant="error" className="mt-2">
              {startError.message}
            </Alert>
          </>
        ) : (
          description
        )
      }
      timer={{
        unit: 'minutes',
        value: 25,
        showOn: STAGES_WITH_CUSTOMER_WORK.includes(stage)
          ? [
              YearEndModuleStatusOptions.upcoming,
              YearEndModuleStatusOptions.actionRequired,
              YearEndModuleStatusOptions.inProgress,
              YearEndModuleStatusOptions.overdue,
              YearEndModuleStatusOptions.upNext,
            ]
          : [],
      }}
      deadlineApproaching={{
        ...defaultDeadlineApproaching,
        displayOn: [
          ...defaultDeadlineApproaching.displayOn,
          YearEndModuleStatusOptions.overdue,
        ],
        alertBody: (() => {
          // Check if we're in overdue status with past due date
          const dateTimeDueDate = dueDate
            ? convertUtcToLocalDate(dueDate)
            : null
          const isPastDueDate =
            dateTimeDueDate && dateTimeDueDate < DateTime.now()
          const isOverdue =
            status === YearEndModuleStatusOptions.overdue && isPastDueDate

          if (isOverdue) {
            // Render overdue alert content
            return (
              <>
                <Text as="bodySm">
                  It looks like you may have missed the{' '}
                  <b>
                    {dueDate
                      ? convertUtcToLocalDate(dueDate)?.toFormat(
                          DATE_FORMATS_LUXON.MONTH_DAY
                        )
                      : 'the internal deadline'}
                  </b>{' '}
                  deadline for filing 1099-NEC through Taxfyle. You still have
                  the opportunity to work with your tax preparer to file, but
                  may owe{' '}
                  <Link
                    href="https://support.joinheard.com/hc/en-us/articles/28063466359191-How-to-avoid-penalties-related-to-1099-NEC"
                    newPage
                    size="small"
                  >
                    late fees
                  </Link>{' '}
                  if not filed with the IRS by{' '}
                  <b>
                    {convertUtcToLocalDate(
                      currentDetails?.form_1099_nec_irs_due_date
                    )?.toFormat(DATE_FORMATS_LUXON.MONTH_DAY) || 'January 31'}
                  </b>
                  .
                </Text>
                <Text as="bodySm" style={{ marginTop: 8 }}>
                  <b>Note:</b> Although Heard covers the cost of your 1099-NEC
                  filings,{' '}
                  <Link
                    href="https://support.joinheard.com/hc/en-us/articles/28063466359191-How-to-avoid-penalties-related-to-1099-NEC"
                    newPage
                    size="small"
                  >
                    late fees
                  </Link>{' '}
                  are not included.
                </Text>
              </>
            )
          }

          // Default deadline approaching content
          return (
            <Text as="bodySm">
              Not filing 1099-NEC&apos;s could incur penalties from the IRS.
              Although Heard covers the cost of your 1099-NEC filings, late fees
              are not included.{' '}
              <Link
                href="https://support.joinheard.com/hc/en-us/articles/28063466359191-How-to-avoid-penalties-related-to-1099-NEC"
                newPage
                size="small"
              >
                More info
              </Link>
            </Text>
          )
        })(),
      }}
      button={{
        text: BUTTON_TEXT[stage],
        disabled: isStarting,
        loading: isStarting || portalLoading,
        error: portalError,
        // Until the job loads the stage can't be known, so offer nothing yet.
        showOn:
          isJobLoading || stage === FilingStage.awaitingPreparer
            ? []
            : BUTTON_STATUSES,
        onActionClick,
      }}
    />
  )
}

export default File1099NECCard
